import { Anchor, Box, Flex, Group, Modal, Pill, Radio, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { NumberInput } from '../../../../components/reactHookFormMantine/NumberInput.tsx';
import { Select } from '../../../../components/reactHookFormMantine/Select.tsx';
import { useI18n } from '../../../../hooks/useI18n.ts';
import { generateRecurrences } from '../../../../lib/generateRecurrences.ts';
import { getNumberWithOrdinal } from '../../../../lib/getNumberWithOrdinal.ts';
import type { IncompleteEventFormComplete } from '../../IncompleteEventForm/schema.ts';
import { getDateDetails } from '../lib/getDateDetails.ts';

const eventDurationPresets = {
  'one-month': 'one month',
  'half-year': 'half year',
  'one-year': 'one year',
  custom: 'custom',
} as const;

const recurrenceOptions: Record<'weekly' | 'bi-weekly' | 'monthly', (keyof typeof eventDurationPresets)[]> = {
  weekly: ['one-month', 'half-year', 'one-year', 'custom'],
  'bi-weekly': ['one-month', 'half-year', 'one-year', 'custom'],
  monthly: ['half-year', 'one-year', 'custom'],
};

const recurrenceDurationWeights: Record<
  'weekly' | 'bi-weekly' | 'monthly',
  Record<keyof typeof eventDurationPresets, number | null>
> = {
  weekly: {
    'one-month': 4,
    'half-year': 26,
    'one-year': 52,
    custom: null,
  },
  'bi-weekly': {
    'one-month': 2,
    'half-year': 13,
    'one-year': 26,
    custom: null,
  },
  monthly: {
    'one-month': 1,
    'half-year': 6,
    'one-year': 12,
    custom: null,
  },
};

export const RecurrenceSettings: FC<{
  useFormValue: UseFormReturn<IncompleteEventFormComplete>;
}> = ({ useFormValue: { control, watch, setValue } }) => {
  const { t, activeLocale } = useI18n();
  const localeLanguagePart = activeLocale?.split('-')?.[0];

  const start = watch('start');
  const selectedRecurrenceDurationOptionValue = watch('recurrenceDurationOption');
  const recurrenceRate = watch('recurrenceRate') ?? 'weekly';
  const noOfEvents = watch('recurrenceAmount');
  const monthRecurrenceType = watch('recurrenceMonthType');

  const startDateDetails = getDateDetails(start);

  const [allDatesModalOpen, setAllDatesModalOpen] = useState(false);

  const durationPresetFallbacks: Record<string, string> = {
    'one-month': 'One month',
    'half-year': 'Half year',
    'one-year': 'One year',
    custom: 'Custom',
  };

  const recurrenceDurationOptions = recurrenceOptions[recurrenceRate].map((preset) => ({
    label: t(`recurrenceSettings_durationPreset_${preset}`) ?? durationPresetFallbacks[preset] ?? preset,
    value: preset,
  }));

  useEffect(() => {
    if (selectedRecurrenceDurationOptionValue === 'one-month' && recurrenceRate === 'monthly') {
      setValue('recurrenceDurationOption', 'half-year');
    }
  }, [recurrenceRate, selectedRecurrenceDurationOptionValue, setValue]);

  useEffect(() => {
    const amount =
      recurrenceDurationWeights[recurrenceRate][
        selectedRecurrenceDurationOptionValue as keyof typeof eventDurationPresets
      ];

    if (amount) {
      setValue('recurrenceAmount', amount);
    }
  }, [recurrenceRate, selectedRecurrenceDurationOptionValue, setValue]);

  const effectiveNoOfEvents =
    noOfEvents ??
    recurrenceDurationWeights[recurrenceRate][
      selectedRecurrenceDurationOptionValue as keyof typeof eventDurationPresets
    ] ??
    null;

  const dates = useMemo(() => {
    return generateRecurrences({
      date: start,
      noOfEvents: effectiveNoOfEvents,
      monthRecurrenceType,
      recurrenceRate,
    });
  }, [monthRecurrenceType, effectiveNoOfEvents, recurrenceRate, start]);

  // Memoized dates summary
  const datesSummary = useMemo(() => {
    if (!dates) {
      return { visibleDates: [], hasMore: false, remaining: 0 };
    }

    const maxVisible = 5;
    const hasMore = dates.length > maxVisible;
    const visibleDates = dates.slice(0, maxVisible);
    const remaining = dates.length - maxVisible;

    return { visibleDates, hasMore, remaining };
  }, [dates]);

  return (
    <Stack gap="xs">
      <Stack gap="xxxs">
        <Text size="xs">{t('recurrenceSettings_repeat') ?? 'Frequency'}</Text>
        <Select
          control={control}
          data={[
            {
              value: 'weekly',
              label: t('recurrenceSettings_repeat_weekly') ?? 'Weekly',
            },
            {
              value: 'bi-weekly',
              label: t('recurrenceSettings_repeat_biWeekly') ?? 'Bi-weekly',
            },
            {
              value: 'monthly',
              label: t('recurrenceSettings_repeat_monthly') ?? 'Monthly',
            },
          ]}
          name="recurrenceRate"
        />
      </Stack>
      {recurrenceRate === 'monthly' && localeLanguagePart && (
        <Stack gap="xxs">
          <Radio
            checked={monthRecurrenceType === 'day-of-month'}
            color="gigi-apple-pink"
            label={t('recurrenceSettings_month_dayOfMonth', {
              day: getNumberWithOrdinal(startDateDetails.dayOfMonth, localeLanguagePart),
            })}
            onChange={() => setValue('recurrenceMonthType', 'day-of-month')}
          />
          {!startDateDetails.isLastWeekdayOccurrence && (
            <Radio
              checked={monthRecurrenceType === 'nth-weekday-of-month'}
              color="gigi-apple-pink"
              label={t('recurrenceSettings_month_nthWeekday', {
                occurrence: getNumberWithOrdinal(
                  startDateDetails.weekdayOccurrenceInMonth.occurrence,
                  localeLanguagePart,
                ),
                weekday: dayjs().day(startDateDetails.weekdayOccurrenceInMonth.dayOfWeek).format('dddd'),
              })}
              onChange={() => setValue('recurrenceMonthType', 'nth-weekday-of-month')}
            />
          )}
          {startDateDetails.isLastWeekdayOccurrence && (
            <Radio
              checked={monthRecurrenceType === 'last-weekday-of-month'}
              label={t('recurrenceSettings_month_lastWeekday', {
                weekday: dayjs().day(startDateDetails.weekdayOccurrenceInMonth.dayOfWeek).format('dddd'),
              })}
              onChange={() => setValue('recurrenceMonthType', 'last-weekday-of-month')}
            />
          )}
        </Stack>
      )}
      <Stack gap="xxxs">
        <Text size="xs">{t('recurrenceSettings_repeatFor') ?? 'Duration'}</Text>
        <Select control={control} data={recurrenceDurationOptions} name="recurrenceDurationOption" />
      </Stack>
      {selectedRecurrenceDurationOptionValue === 'custom' && (
        <Stack gap="xxxs">
          <Text size="xs">{t('recurrenceSettings_numberOfEvents') ?? 'Number of events'}</Text>

          <NumberInput control={control} name="recurrenceAmount" />
        </Stack>
      )}

      {dates && datesSummary && (
        <Stack gap="xxs">
          <Text size="xs">
            {t('recurrenceSettings_datesOfEvents') ?? 'Upcoming dates'} ({effectiveNoOfEvents ?? 0}):
          </Text>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--mantine-spacing-xxxs)',
            }}
          >
            {datesSummary.visibleDates.map((date) => (
              <Pill
                key={date.toString()}
                size="xs"
                styles={{ root: { backgroundColor: '#F4F4F4', color: '#191919', justifyContent: 'center' } }}
              >
                {dayjs(date).format('ddd, D MMM YYYY')}
              </Pill>
            ))}
            {datesSummary.hasMore && (
              <Anchor
                c="grape"
                component="button"
                size="xs"
                style={{ textAlign: 'center' }}
                type="button"
                onClick={() => setAllDatesModalOpen(true)}
              >
                {t('recurrenceSettings_dates_more', {
                  count: datesSummary.remaining?.toString(),
                }) ?? `+${datesSummary.remaining} more events`}
              </Anchor>
            )}
          </Box>
          {datesSummary.hasMore && (
            <Group align="center" gap="xxs" mt="xxs">
              <Text size="xs">Last recurring event:</Text>
              <Pill size="xs" styles={{ root: { backgroundColor: '#7C3AED', color: '#fff' } }}>
                {dayjs(dates.at(-1)).format('ddd, D MMM YYYY')}
              </Pill>
            </Group>
          )}
        </Stack>
      )}

      <Modal
        centered
        opened={allDatesModalOpen}
        size="70%"
        styles={{
          body: { padding: 'var(--mantine-spacing-xs)', paddingBottom: 'var(--mantine-spacing-xs)' },
          header: { padding: 'var(--mantine-spacing-xs)' },
        }}
        title={`All recurring dates (${dates?.length ?? 0})`}
        onClose={() => setAllDatesModalOpen(false)}
      >
        <Flex gap="md">
          {/* Left: Calendars */}
          <ScrollArea.Autosize mah={600} miw={280} style={{ flex: '0 0 280px' }}>
            <Stack gap="md">
              {(() => {
                if (!dates || dates.length === 0) return null;
                const eventDateStrings = new Set(dates.map((d) => dayjs(d).format('YYYY-MM-DD')));
                const firstMonth = dayjs(dates[0]).startOf('month');
                const lastMonth = dayjs(dates.at(-1)).startOf('month');
                const months: Date[] = [];
                let current = firstMonth;
                while (current.isBefore(lastMonth) || current.isSame(lastMonth, 'month')) {
                  months.push(current.toDate());
                  current = current.add(1, 'month');
                }
                return months.map((month) => (
                  <Calendar
                    hideOutsideDates
                    static
                    date={month}
                    getDayProps={(date) => ({
                      style: eventDateStrings.has(dayjs(date).format('YYYY-MM-DD'))
                        ? { backgroundColor: '#7C3AED', color: '#fff', borderRadius: '50%' }
                        : undefined,
                    })}
                    key={dayjs(month).format('YYYY-MM')}
                    styles={{
                      calendarHeader: { maxWidth: '100%' },
                      calendarHeaderControl: { display: 'none' },
                    }}
                    withCellSpacing={false}
                  />
                ));
              })()}
            </Stack>
          </ScrollArea.Autosize>

          {/* Divider */}
          <Box style={{ width: 1, backgroundColor: 'var(--mantine-color-gray-3)', flexShrink: 0 }} />

          {/* Right: Table */}
          <ScrollArea.Autosize mah={600} style={{ flex: 1 }}>
            <Table striped verticalSpacing="xxxs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Day</Table.Th>
                  <Table.Th>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dates?.map((date, index) => (
                  <Table.Tr key={date.toString()}>
                    <Table.Td c="gray.5">{index + 1}</Table.Td>
                    <Table.Td>{dayjs(date).format('dddd')}</Table.Td>
                    <Table.Td>{dayjs(date).format('D MMM YYYY')}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Flex>
      </Modal>
    </Stack>
  );
};
